import React, { useRef } from 'react';
import { Icon, Spin, Tooltip } from 'antd';
import { Document, Page } from 'react-pdf';
import { useDrag, useDrop } from 'react-dnd';
import {
  PDFPreview,
  PDFHeader,
  ExitButton,
  UpdateAddressButton,
  PDFContainer,
  WindowGuide,
  AddressOverlay,
  AddressTextarea,
  NavigationButton,
  LoaderContainer,
  ReturnButton,
} from '../HybridMailStyles';
import { useHybridMail, PDF_SCALE, ENVELOPE_TYPES, scaleValue } from '../HybridMailContext';
import { HYBRID_MAIL } from '../TranslationValue';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import moment from 'moment';

const ItemTypes = {
  ADDRESS: 'ADDRESS',
};

const DraggableAddress = ({ x, y, valid, dragSize, fontStyle, addressText, setAddressText }) => {
  const ref = useRef(null);

  const [{ isDragging }, drag] = useDrag({
    item: { type: ItemTypes.ADDRESS, x: scaleValue(x), y: scaleValue(y)-12 },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(ref);

  return (
    <AddressOverlay
      ref={ref}
      x={scaleValue(x)}
      y={scaleValue(y) -12}
      valid={valid}
      style={{
        opacity: isDragging ? 0.2 : 1,
        cursor: 'move',
        ...dragSize,
      }}
    >
      <AddressTextarea
        fontStyle={fontStyle}
        scale={PDF_SCALE}
        value={addressText}
        onChange={e => setAddressText(e.target.value)}
        style={{ width: dragSize.width, height: dragSize.height - 4 }}
      />
    </AddressOverlay>
  );
};

const PDFDropLayer = ({ children, updateXYByIndex }) => {
  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: ItemTypes.ADDRESS,
    collect: monitor => !!monitor.isOver({ shallow: true }),
    drop: (item, monitor) => {
      const delta = monitor.getDifferenceFromInitialOffset();
      if (!delta) return;

      requestAnimationFrame(() => {
        const x = Math.round(item.x + delta.x);
        const y = Math.round(item.y + delta.y);
        updateXYByIndex(x, y);
      });
    },
  });

  drop(ref);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {children}
    </div>
  );
};

const MemoizedPDFPage = React.memo(({ pageNumber, onPageRenderSuccess }) => {
  return (
    <Page
      pageNumber={pageNumber}
      // onRenderSuccess={onPageRenderSuccess}
      scale={PDF_SCALE}
      renderTextLayer={true}
      renderAnnotationLayer={true}
      renderForms={true}
      loading=""
      error=""
    />
  );
});

const PDFPreviewArea = ({ setCurrentPage,setFilterValues, setFilters, setRequiresMyApproval }) => {
  const {
    isPreview,
    isLoading,
    handleExitPreview,
    selectedText,
    handleProcessSelectedText,
    selectedFile,
    envelopeType,
    selectedBlockIndex,
    isValidPos,
    dragSize,
    fontStyle,
    addressText,
    updateAddressText,
    updateXYByIndex,
    handleNavigation,
    addressBlocks,
    handleTextSelection,
    numPages,
    setNumPages,
    setIsLoading,
    position,
    pdfContainerRef,
    t,
  } = useHybridMail();

  const handleReturnClick = () =>{
    setFilterValues({});
    const startDate = moment()
      .subtract(7, 'day')
      .startOf('day');
    const endDate = moment().endOf('day');
    const formattedStartDate = startDate.format('YYYY-MM-DDTHH:mm:ss');
    const formattedEndDate = endDate.format('YYYY-MM-DDTHH:mm:ss');
    setFilters({filter: { page: 1, startDate: formattedStartDate, endDate: formattedEndDate , limit: 4},});
    setRequiresMyApproval(false);
    setCurrentPage('dashboard');
  }
  const { x, y, ...rest } = ENVELOPE_TYPES[envelopeType];

  // const env = ENVELOPE_TYPES[envelopeType];
  // const positionMap = useHybridMail().positionMap;
  // const pos = positionMap.get(selectedBlockIndex);

  return (
    <PDFPreview>
      <PDFHeader>
        {isPreview && (
          <ExitButton onClick={handleExitPreview}>
            <Icon type="close" />
            {t(HYBRID_MAIL.EXIT_PREVIEW.key, HYBRID_MAIL.EXIT_PREVIEW.value)}
          </ExitButton>
        )}

        {!isPreview && selectedText && (
          <UpdateAddressButton 
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            onClick={handleProcessSelectedText}>
            <Tooltip
              placement="topLeft"
              title={t(
                HYBRID_MAIL.TOOLTIP_PROCESS_SELECTED_TEXT.key,
                HYBRID_MAIL.TOOLTIP_PROCESS_SELECTED_TEXT.value,
              )}
            >
              <Icon type="info-circle" />
            </Tooltip>
            {t(HYBRID_MAIL.PROCESS_SELECTED_TEXT.key, HYBRID_MAIL.PROCESS_SELECTED_TEXT.value)}
          </UpdateAddressButton>
        )}
        <ReturnButton onClick={() =>{handleReturnClick()}}>
          <Icon type="left" />
          {t(HYBRID_MAIL.RETURN_TO_DASHBOARD.key, HYBRID_MAIL.RETURN_TO_DASHBOARD.value)}
        </ReturnButton>
      </PDFHeader>
      {isLoading && (
        <LoaderContainer>
          <Spin size="small" /> {t(HYBRID_MAIL.LOADING_TEXT.key, HYBRID_MAIL.LOADING_TEXT.value)}
        </LoaderContainer>
      )}
      <PDFContainer ref={pdfContainerRef}>
        {selectedFile ? (
          <Document
            file={selectedFile}
            onLoadSuccess={({ numPages }) => {
              setNumPages(numPages);
              setIsLoading(false);
            }}
            loading=""
            error=""
            onLoadStart={() => setIsLoading(true)}
          >
            {Array.from(new Array(numPages), (el, index) => (
              <PDFDropLayer
                key={index}
                // setAddressPos={setPositionMap}
                updateXYByIndex={updateXYByIndex}
              >
                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '0px 0 15px 0',
                  }}
                >
                  <div
                    style={{
                      border: 'solid 1px #e3e3e3',
                      display: 'inline-block',
                      position: 'relative',
                    }}
                  >
                    <MemoizedPDFPage pageNumber={index + 1} />

                    {numPages && index === 0 && !isPreview && position && (
                      <WindowGuide {...rest} x={scaleValue(x)} y={scaleValue(y)}/>
                    )}

                    {numPages && index === 0 && !isPreview && position && (
                      <DraggableAddress
                        x={position.x}
                        y={position.y}
                        valid={isValidPos}
                        dragSize={dragSize}
                        fontStyle={fontStyle}
                        addressText={addressText}
                        setAddressText={updateAddressText}
                      />
                    )}
                  </div>
                </div>
              </PDFDropLayer>
            ))}
          </Document>
        ) : null}
      </PDFContainer>
    </PDFPreview>
  );
};

export default PDFPreviewArea;
